/**
 * "Run scan" CTA. Lets the operator pick which subset of tracked competitors
 * to scan; defaults to "all" when there are 10 or fewer.
 */
import { useState } from 'react';
import { Zap } from 'lucide-react';
import { toast } from 'sonner';
import type { Competitor } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/spinner';
import { summarizeApiError } from '@/lib/api';
import { useCreateCompetitorRun } from '@/features/competitors/use-create-competitor-run';
import { cn } from '@/lib/cn';

export interface CompetitorRunButtonProps {
  projectId: string;
  competitors: Competitor[];
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  onStarted?: (runId: string) => void;
}

export function CompetitorRunButton({
  projectId,
  competitors,
  buttonProps,
  onStarted,
}: CompetitorRunButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(competitors.map((c) => c.id)),
  );
  const create = useCreateCompetitorRun();
  const disabled = !projectId || competitors.length === 0;

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStart = async (): Promise<void> => {
    if (selected.size === 0) {
      toast.error('Pick at least one competitor');
      return;
    }
    try {
      const run = await create.mutateAsync({
        project_id: projectId,
        competitor_ids: [...selected],
      });
      toast.success('Competitor scan started', {
        description: `Scanning ${selected.size} competitor${selected.size === 1 ? '' : 's'}`,
      });
      setOpen(false);
      onStarted?.(run.id);
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not start scan',
        fieldLabels: { project_id: 'Project', competitor_ids: 'Competitors' },
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) setSelected(new Set(competitors.map((c) => c.id)));
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>
        <Button {...buttonProps} disabled={disabled || buttonProps?.disabled}>
          <Zap className="h-4 w-4" />
          Run scan
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" /> Start a competitor scan
          </DialogTitle>
          <DialogDescription>
            We'll fetch each competitor's sitemap, classify any new pages, and
            produce a digest with recommendations. Takes 1–2 minutes per competitor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Competitors to scan
            </Label>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setSelected(new Set(competitors.map((c) => c.id)))}
              >
                Select all
              </button>
              <span className="text-muted-foreground">·</span>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setSelected(new Set())}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="max-h-64 space-y-1.5 overflow-auto rounded-md border border-border/70 bg-surface-2 p-2">
            {competitors.length === 0 ? (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No competitors tracked for this project yet.
              </div>
            ) : (
              competitors.map((c) => {
                const checked = selected.has(c.id);
                return (
                  <label
                    key={c.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-md border border-transparent px-2 py-1.5 text-sm transition-colors',
                      checked
                        ? 'border-primary/40 bg-primary/10'
                        : 'hover:border-border/70 hover:bg-card',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                      className="h-4 w-4 rounded border-border/70 accent-primary"
                    />
                    <span className="font-mono">{c.domain}</span>
                    {c.label ? (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {c.label}
                      </span>
                    ) : null}
                  </label>
                );
              })
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            {selected.size === 0
              ? 'Pick at least one competitor.'
              : `${selected.size} of ${competitors.length} selected.`}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleStart}
            disabled={selected.size === 0 || create.isPending}
          >
            {create.isPending ? <Spinner className="mr-2" /> : null}
            Start scan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
